import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Gift, Copy, Check, Twitter, Linkedin, Mail, Share2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function makeAnonReferralCode() {
  let code = localStorage.getItem('tfu_ref_code');
  if (!code) {
    code = 'TFU-' + Math.random().toString(36).slice(2, 8).toUpperCase();
    localStorage.setItem('tfu_ref_code', code);
  }
  return code;
}

export default function EarnCreditsModal({ open, onOpenChange }) {
  const { user, login } = useAuth();
  const [copied, setCopied] = useState(false);

  // Authenticated users get their server-issued referral_code; otherwise show a
  // disclaimer that they need to sign in for rewards to actually be granted.
  const code = useMemo(
    () => user?.referral_code || makeAnonReferralCode(),
    [user]
  );
  const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/?ref=${code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select the link manually.');
    }
  };

  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      'I just shipped a full-stack app with Transformerus in minutes. Use my link and you get $200 in credits, I do too:'
    )}&url=${encodeURIComponent(link)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
    email: `mailto:?subject=${encodeURIComponent('Build your next app on Transformerus')}&body=${encodeURIComponent(
      'I have been using Transformerus to ship full-stack apps in minutes. Sign up with my link and we both get $200 in credits: ' + link
    )}`,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white">
        <div className="relative px-7 pt-8 pb-7 bg-gradient-to-br from-emerald-50 via-cyan-50 to-sky-50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(167,243,208,0.6),transparent_60%)]" />
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-white shadow-md flex items-center justify-center mb-4">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-bold text-neutral-900 tracking-tight">
                Earn $200 in credits
              </DialogTitle>
              <DialogDescription className="text-neutral-700 text-[15px] mt-2">
                Invite a builder to Transformerus. When they make their first paid purchase, you both get
                <span className="font-semibold text-neutral-900"> $200 </span>
                in build credits, instantly.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="px-7 py-6 space-y-4">
          {!user && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
              <LogIn className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="font-semibold">Sign in to start earning rewards.</span>
                <span> Anonymous links are sharable, but actual credits are only granted to a signed-in account.</span>
              </div>
              <Button onClick={login} size="sm" className="h-7 px-2.5 text-xs bg-amber-600 hover:bg-amber-700">
                Sign in
              </Button>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              Your referral link
            </label>
            <div className="mt-2 flex gap-2">
              <Input value={link} readOnly className="flex-1 text-sm bg-neutral-50 border-neutral-200" />
              <Button onClick={copy} className="h-10 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-4">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-neutral-500 mt-1.5">
              Code: <span className="font-mono text-neutral-800">{code}</span>
            </p>
          </div>

          <div className="pt-2">
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              Share via
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <a href={shareUrls.twitter} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 h-11 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors">
                <Twitter className="w-4 h-4" /> Twitter
              </a>
              <a href={shareUrls.linkedin} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 h-11 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors">
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              <a href={shareUrls.email}
                className="flex items-center justify-center gap-2 h-11 rounded-xl bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-sm text-neutral-700 transition-colors">
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>
          </div>

          <div className="pt-4 border-t border-neutral-100">
            <div className="flex items-start gap-3 text-xs text-neutral-600">
              <Share2 className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
              <p>
                Credits are awarded after the referred builder completes their first paid purchase. Limit
                of 25 successful referrals per user. Full terms apply.
              </p>
            </div>
            {user && (
              <a href="/referrals" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-neutral-900 hover:text-neutral-700">
                View full dashboard <span aria-hidden>→</span>
              </a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
